import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db, default as app } from '../firebase';
import { signInWithEmailAndPassword, signInAnonymously, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from "firebase/functions";
import { Form, Button, Container, Row, Col, Card, Alert, InputGroup, Spinner, ListGroup } from 'react-bootstrap';
import { Eye, EyeSlash, Clock } from 'react-bootstrap-icons';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({ type: '', content: '' });
  const [isReady, setIsReady] = useState(false);

  const navigate = useNavigate();

  // ① キャッシュクリア（リロード）処理
  useEffect(() => {
    const hasRefreshed = sessionStorage.getItem('login_cache_cleared');
    if (!hasRefreshed) {
      sessionStorage.setItem('login_cache_cleared', 'true');
      window.location.reload(true);
    } else {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    document.title = 'ログイン | My Order System';
  }, []);

  // ② メンテナンス状態の監視
  useEffect(() => {
    if (!isReady) return;
    const sessionDocRef = doc(db, "system_settings", "session_control");
    const unsubscribe = onSnapshot(sessionDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsMaintenance(true);
        setMaintenanceInfo({
          type: data.type || 'システムメンテナンス',
          content: data.content || ''
        });
      } else {
        setIsMaintenance(false);
        setMaintenanceInfo({ type: '', content: '' });
      }
    }, (err) => {
      console.warn("Maintenance listener error:", err);
    });
    return () => unsubscribe();
  }, [isReady]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }
    setIsLoggingIn(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists() && userDocSnap.data().status === 'suspended') {
        await signOut(auth);
        setError('このアカウントは現在停止されています。管理者にお問い合わせください。');
        setIsLoggingIn(false);
        return;
      }

      // メンテナンスチェック：管理者でない場合は弾く
      if (isMaintenance && userDocSnap.exists() && userDocSnap.data().userAttribute !== '管理者') {
        await signOut(auth);
        setError('只今メンテナンス中のため、一般ユーザーの方はログインできません。');
        setIsLoggingIn(false);
        return;
      }

      try {
        const functions = getFunctions(app, 'asia-northeast1');
        const logLogin = httpsCallable(functions, 'logLogin');
        await logLogin();
      } catch (logError) {
        console.error("ログインログの記録に失敗しました:", logError);
      }
      navigate('/mainmenu');

    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/invalid-email') {
        setError('メールアドレスまたはパスワードが間違っています。');
      } else {
        setError('ログインに失敗しました。もう一度お試しください。');
      }
      setIsLoggingIn(false);
    }
  };

  const handleAnonymousLogin = async () => {
    if (isMaintenance) {
      setError('只今メンテナンス中のため、ご利用いただけません。');
      return;
    }
    setError('');
    setIsLoggingIn(true);
    try {
      await signInAnonymously(auth);
      navigate('/mainmenu');
    } catch (err) {
      setError('匿名ログインに失敗しました。もう一度お試しください。');
      setIsLoggingIn(false);
    }
  };

  const handlePasswordReset = async () => {
    setError('');
    setMessage('');
    if (!email) {
      setError('パスワードをリセットするには、メールアドレスを入力してください。');
      return;
    }
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true,
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setMessage('パスワード再設定用のメールを送信しました。受信トレイをご確認ください。');
    } catch (err) {
      setError('メールの送信に失敗しました。');
    }
  };

  if (!isReady) {
    return <div className="text-center my-5">Loading...</div>;
  }

  return (
    <Container className="my-5">
      <div className="text-center mb-4">
        <p className="mb-1 text-muted">2026年1月21日（水）リリース</p>
        <h1 className="h2">新 旭興・西武サンプルオーダーシステム</h1>
      </div>

      {isMaintenance && (
        <Alert variant="warning" className="text-center mb-4 shadow-sm border-warning p-4">
          <div className="d-flex align-items-center justify-content-center mb-2">
            <Clock className="me-2 text-danger" size={24} />
            <strong className="h5 mb-0 text-danger fw-bold">【{maintenanceInfo.type}】只今メンテナンス中です。</strong>
          </div>
          {maintenanceInfo.content && (
            <div className="mt-3 p-3 bg-light rounded border border-secondary-subtle text-dark mx-auto" style={{ maxWidth: '600px', whiteSpace: 'pre-wrap' }}>
              {maintenanceInfo.content}
            </div>
          )}
          <div className="mt-3 small text-dark">
            現在、管理者の操作によりログインを一時的に制限しております。<br />
            作業終了まで今しばらくお待ちください。
          </div>
        </Alert>
      )}

      <Row>
        <Col md={6} className="mb-4 mb-md-0">
          <div className="border rounded p-4 h-100 d-flex flex-column shadow-sm">
            <h3 className="h5">会員のお客様</h3>
            <p className="text-muted small">
              ご登録済みの方はこちらよりログイン下さい。
              <br />
              旧システムをご利用の方は、初回のみアカウント移行とパスワード再設定が必要です。
              <br />
              ※移行完了後にログイン可能となります
            </p>
            <Link 
              to="/forgot-password" 
              className="ms-1 fw-bold"
              style={{ pointerEvents: isMaintenance ? 'none' : 'auto', color: isMaintenance ? 'gray' : '' }}
            >
              旧システムからのアカウント移行（パスワード再設定）
            </Link>
            <hr />
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleLogin}>
              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Label>メールアドレス</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="メールアドレスを入力"
                  autoComplete="email"
                  // ★ 変更：メンテナンス中でも入力自体は常に許可する
                  disabled={isLoggingIn} 
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formBasicPassword">
                <Form.Label>パスワード</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={passwordVisible ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワード"
                    autoComplete="current-password"
                    // ★ 変更：メンテナンス中でも入力自体は常に許可する
                    disabled={isLoggingIn}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    {passwordVisible ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
              </Form.Group>

              <div className="d-grid">
                {/* ★ 変更：disabledからisMaintenanceを除外。管理者はメンテナンス中でも押せるようにする */}
                <Button variant="primary" type="submit" disabled={isLoggingIn}>
                  {isLoggingIn ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      <span className="ms-2">ログイン中...</span>
                    </>
                  ) : (isMaintenance ? 'ログイン' : 'ログイン')}
                </Button>
              </div>
              <div className="py-3">
                {message && <Alert variant="success">{message}</Alert>}
              </div>
              <div className="text-center mt-3">
                <Link 
                  to="#" 
                  onClick={handlePasswordReset}
                  style={{ pointerEvents: isMaintenance ? 'none' : 'auto', color: isMaintenance ? 'gray' : '' }}
                >
                  パスワードをお忘れですか？
                </Link>
              </div>
            </Form>
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-4 h-100 d-flex flex-column shadow-sm">
            <h3 className="h5">初めてご利用のお客様</h3>
            <p className="text-muted small">会員登録がまだお済みでないお客様は以下より新規会員登録をお願い致します。</p>
            <hr />
            <div className="bg-light p-3 rounded mb-3">
              <p className="mb-2"><strong>登録すると便利な機能がご利用頂けます。</strong></p>
              <ListGroup variant="flush" className="small">
                <ListGroup.Item className="bg-transparent px-0 py-1">・登録した送付先住所は再入力せずに活用できます。</ListGroup.Item>
                <ListGroup.Item className="bg-transparent px-0 py-1">・オーダー完了時に受付メールが届きます。</ListGroup.Item>
                <ListGroup.Item className="bg-transparent px-0 py-1">・オーダー履歴より、配送状況の確認が行えます。</ListGroup.Item>
              </ListGroup>
            </div>
            <div className="d-grid gap-2 mt-auto">
              {/* 新規登録はメンテナンス中なら完全に無効化 */}
              <Button variant="success" onClick={() => navigate('/signup')} disabled={isMaintenance}>
                新規会員登録
              </Button>
              <Button variant="secondary" onClick={handleAnonymousLogin} disabled={isLoggingIn || isMaintenance}>
                {isMaintenance ? 'メンテナンス中' : '登録せずに注文する'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <Card className="shadow-sm border-warning">
            <Card.Body className="p-4">
              <h4 className="h5 text-center fw-bold">
                2026年1月21日（水）リリース<br />
                新旭興・西武サンプルオーダーシステム
              </h4>
              <p className="mb-2">
                前システムでのアカウント登録がお済みのお客様は、下記の手順でアカウントの引き継ぎをお願い致します。
              </p>
              <ListGroup variant="flush" className="mb-3">
                <ListGroup.Item className="px-0 py-1">
                  ❶ ログインフォームにアドレスを入力して
                  <br />
                  <Link to="/forgot-password" className="ms-1 fw-bold">
                    旧システムからのアカウント移行（パスワード再設定）
                  </Link>
                  をクリック
                </ListGroup.Item>
              </ListGroup>
              <p className="mb-2">
                ❷ ご登録のアドレスにパスワードの再設定用のメールが届きます。<br />
                ❸ URLのリンクをクリックし、パスワードの再設定をお願い致します。
              </p>
              <p className="small text-muted mb-2">
                ※パスワード再設定メールが届かない場合は、以下の手順をご確認ください。<br />
                1. 迷惑メールフォルダ等に振り分けられていないかご確認ください。<br />
                {/* 2. 「@sample-order-system.firebaseapp.com」からのメールを受信できる設定になっているかご確認ください。 */}
                2. 「@samplecenter.seko.co.jp」からのメールを受信できる設定になっているかご確認ください。
              </p>
              <p className="small text-muted mb-0">
                上記をご確認いただいても再設定メールが届かない場合はお手数ですが、新システム上でのアカウント再登録をお願いいたします。<br />
                再登録後は、すぐにログインが可能となります。
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="text-center mt-4">
        <a href="https://www.seko.co.jp/inq/index.html" target="_blank" rel="noopener noreferrer">
          お問い合わせ
        </a>
      </div>
    </Container>
  );
}

export default Login;